import PropTypes from "prop-types"
import React, { useState, useEffect } from "react"
import { useQuery } from "urql"

import FooterSection from "./components/FooterSection"
import HeaderSection from "./components/HeaderSection"
import ServiceAvailableTimes from "./components/ServiceAvailableTimes"
import ServiceDetails from "./components/ServiceDetails"
import ServiceLocation from "./components/ServiceLocation"
import ServicePractitioner from "./components/ServicePractitioner"
import ServiceTimeSearchBar from "./components/ServiceTimeSearchBar"

const AVAILABILITY_QUERY = `
  query bookingAvailability($serviceId: ID!, $locationId: ID!, $page: Int) {
    bookingAvailability(serviceId: $serviceId, locationId: $locationId, page: $page) {
      timeZone
      availableSlots { date datetimes }
      lastDateInPage
    }
  }
`

const ServicePageV2Desktop = ({ practice, service, theme }) => {
  const [selectedDuration, setSelectedDuration] = useState(service)
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [currentPage, setCurrentPage] = useState(0)
  const [allAvailableTimes, setAllAvailableTimes] = useState([])
  const [loadingMore, setLoadingMore] = useState(false)
  const [selectedTime, setSelectedTime] = useState(null)

  // Create mock section configurations for header and footer
  const headerSection = {
    id: "service-page-header",
    type: "header",
    settings: {
      showLogo: true,
      showCTA: true,
      ctaText: "Book Now",
      ctaAction: "booking",
      showPromoCTA: true,
      promoCTAText: "Free discovery call",
      promoCTAAction: "booking",
      showNavigation: true,
      showSocialLinks: true
    }
  }

  const footerSection = {
    id: "service-page-footer",
    type: "footer",
    settings: {
      showLogo: true,
      showCTA: true,
      ctaText: "Book a Free Discovery Call",
      ctaAction: "booking",
      showSocialLinks: true,
      showPoweredBy: true
    }
  }

  // Set default selections based on service data
  useEffect(() => {
    // Set default duration (always start with the original service)
    setSelectedDuration({
      id: service.id,
      name: service.name,
      amountCents: service.amountCents,
      timeLength: service.timeLength
    })

    // Set default location
    if (service.locations && service.locations.length > 0) {
      const defaultLocation = service.locations.find((loc) => loc.kind === "physical") || service.locations[0]
      setSelectedLocation(defaultLocation)
    }
  }, [service])

  // Get the service ID to use for booking (could be main service or selected variation)
  const bookingServiceId = selectedDuration?.id || service.id

  // Fetch availability data from GraphQL
  const [{ data: availabilityData, fetching: loadingAvailability, error: availabilityError }, refetchAvailability] =
    useQuery({
      query: AVAILABILITY_QUERY,
      variables: {
        serviceId: bookingServiceId,
        locationId: selectedLocation?.id,
        page: currentPage
      },
      pause: !bookingServiceId || !selectedLocation?.id
    })

  const timeZone = availabilityData?.bookingAvailability?.timeZone
  const lastDateInPage = availabilityData?.bookingAvailability?.lastDateInPage

  // Accumulate available times when new data is loaded
  useEffect(() => {
    if (availabilityData?.bookingAvailability?.availableSlots) {
      const newAvailableTimes = availabilityData.bookingAvailability.availableSlots.map((slot) => ({
        date: slot.date,
        times: slot.datetimes
      }))

      if (currentPage === 0) {
        // Reset for first page
        setAllAvailableTimes(newAvailableTimes)
      } else {
        // Append new times to existing ones
        setAllAvailableTimes((prev) => {
          const existingDates = new Set(prev.map((day) => day.date))
          const newTimes = newAvailableTimes.filter((day) => !existingDates.has(day.date))
          return [...prev, ...newTimes]
        })
      }
      // Reset loading more state
      setLoadingMore(false)
    }
  }, [availabilityData, currentPage])

  // Reset pagination when location or duration changes
  useEffect(() => {
    setCurrentPage(0)
    setAllAvailableTimes([])
  }, [selectedLocation?.id, selectedDuration?.id])

  // Log availability errors
  if (availabilityError) {
    // eslint-disable-next-line no-console
    console.error("Availability query error:", availabilityError)
  }

  // Refetch availability when duration or location changes
  useEffect(() => {
    if (selectedDuration && selectedLocation) {
      refetchAvailability({ requestPolicy: "network-only" })
    }
  }, [selectedDuration, selectedLocation, currentPage, refetchAvailability])

  // Determine if service has multiple durations or locations
  const hasMultipleDurations = service.variations && service.variations.length > 1
  const hasMultipleLocations = service.locations && service.locations.length > 1

  // Handle duration change with refetch
  const handleDurationChange = (newDuration) => {
    setSelectedDuration(newDuration)
  }

  // Handle location change with refetch
  const handleLocationChange = (newLocation) => {
    setSelectedLocation(newLocation)
  }

  // Handle loading more times
  const handleLoadMoreTimes = () => {
    setLoadingMore(true)
    setCurrentPage((prev) => prev + 1)
  }

  // Handle time selection
  const handleTimeSelect = (selection) => {
    setSelectedTime(selection)

    // Automatically redirect to checkout page with selected information
    // The time field already contains the full datetime with timezone
    const startTime = selection.time

    const encodedStartTime = encodeURIComponent(startTime)

    // Build checkout URL with required parameters
    const checkoutUrl = `/checkout?pid=${practice.id}&sid=${bookingServiceId}&lid=${selectedLocation.id}&st=${encodedStartTime}`

    // Redirect to checkout page
    window.location.href = checkoutUrl
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme?.colors?.bgLight || "#FFFFFF" }}>
      {/* Header Section */}
      <HeaderSection section={headerSection} practice={practice} theme={theme} deviceType="desktop" />

      {/* Hero Section with Service Info */}
      <div className="relative pb-20 pt-3" style={{ backgroundColor: theme?.colors?.bgLight || "#f4ece9" }}>
        <div className="mx-auto max-w-[1440px] px-8">
          {/* Practitioner & Service Title */}
          <div className="flex flex-col items-center gap-4 px-[390px] py-12">
            <div className="flex items-center gap-3">
              <div
                className="h-12 w-12 rounded-full bg-cover bg-center"
                style={{
                  backgroundImage: practice.user?.profilePhotoUrl ? `url(${practice.user.profilePhotoUrl})` : "none",
                  backgroundColor: practice.user?.profilePhotoUrl ? "transparent" : theme?.colors?.greyLight || "#ccc"
                }}
              />
              <div className="flex flex-col gap-0.5">
                <h2
                  className="text-[20px] font-bold leading-[32px]"
                  style={{
                    fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
                    color: theme?.colors?.textDark || "#55433f"
                  }}>
                  {practice.user?.name || practice.name}
                </h2>
                <div className="flex items-center gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <div
                      key={i}
                      className="h-4 w-4"
                      style={{
                        color:
                          i < Math.floor(practice.reviewsAverage || 0)
                            ? theme?.colors?.reviewStars || "#F4B965"
                            : "#E5E5E5"
                      }}>
                      ★
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Service Title */}
            <h1
              className="max-w-[660px] text-center text-[56px] font-bold leading-[72px]"
              style={{
                fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
                color: theme?.colors?.textDark || "#55433f"
              }}>
              {service.name}
            </h1>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <div className="-mb-16 h-20" style={{ backgroundColor: theme?.colors?.bgLight || "#f4ece9" }} />
            <div className="absolute bottom-0 left-1/2 w-[872px] -translate-x-1/2">
              <ServiceTimeSearchBar
                service={service}
                selectedDuration={selectedDuration}
                selectedLocation={selectedLocation}
                onDurationChange={handleDurationChange}
                onLocationChange={handleLocationChange}
                hasMultipleDurations={hasMultipleDurations}
                hasMultipleLocations={hasMultipleLocations}
                theme={theme}
                deviceType="desktop"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-auto flex max-w-[760px] flex-col gap-8 px-5 pb-48 pt-16">
        {/* Available Times */}
        <ServiceAvailableTimes
          availableTimes={allAvailableTimes}
          selectedLocation={selectedLocation}
          loading={loadingAvailability}
          error={availabilityError}
          timeZone={timeZone}
          practice={practice}
          bookingServiceId={bookingServiceId}
          onLoadMore={handleLoadMoreTimes}
          hasMoreData={!!lastDateInPage}
          loadingMore={loadingMore}
          onTimeSelect={handleTimeSelect}
          selectedTime={selectedTime}
          theme={theme}
        />

        {/* Service Details */}
        <ServiceDetails service={service} practice={practice} theme={theme} />

        {/* Location */}
        <ServiceLocation
          locations={service.locations}
          selectedLocation={selectedLocation}
          practice={practice}
          theme={theme}
        />

        {/* Practitioner */}
        <ServicePractitioner practice={practice} theme={theme} />
      </div>

      {/* Footer */}
      <FooterSection section={footerSection} practice={practice} theme={theme} deviceType="desktop" />
    </div>
  )
}

ServicePageV2Desktop.propTypes = {
  practice: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    practitionerName: PropTypes.string,
    practitionerImage: PropTypes.string,
    user: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      title: PropTypes.string,
      profilePhotoUrl: PropTypes.string
    }),
    reviewsAverage: PropTypes.number,
    reviewsCount: PropTypes.number,
    location: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        city: PropTypes.string,
        state: PropTypes.string
      })
    ]),
    locations: PropTypes.arrayOf(PropTypes.object),
    pages: PropTypes.arrayOf(PropTypes.object),
    socialLinks: PropTypes.object
  }).isRequired,
  service: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    amountCents: PropTypes.number,
    timeLength: PropTypes.number,
    imageUrl: PropTypes.string,
    locations: PropTypes.arrayOf(PropTypes.object),
    variations: PropTypes.arrayOf(PropTypes.object)
  }).isRequired,
  theme: PropTypes.object
}

export default ServicePageV2Desktop
